import React, { useState, useEffect, useMemo } from "react";
import { updateUserProfile } from "../../../graphql/client";
import "./UserInfoModal.css";
import { Radio, Select, Form } from "antd";
import classnames from "classnames";

const UserInfoModal = ({
  onClose,
  onSubmit,
  email,
  userInfo,
  cognitoToken,
  featureType,
}) => {
  const [phone, setPhone] = useState(userInfo?.phone || "");
  const [sex, setSex] = useState(userInfo?.sex || "male");
  const [shirtSize, setShirtSize] = useState(userInfo?.shirtSize || "M");
  const [interestedEvents, setInterestedEvents] = useState(
    userInfo?.interestedEvents || ""
  );
  const [referredBy, setReferredBy] = useState(userInfo?.referredBy || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [currentEvents, setCurrentEvents] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState({});

  // Determine if phone and sex fields should be readonly based on existing values and edit mode
  const isEditMode = featureType === "profile";
  const isPhoneReadOnly = isEditMode && userInfo?.phone;
  // Sex is answer-once in EVERY mode (not just profile edit): it gates
  // pricing, so once it's male/female the field locks. The backend
  // enforces the same rule; this is just the honest UI for it.
  const isSexReadOnly =
    userInfo?.sex === "male" || userInfo?.sex === "female";
  const shouldDisable = isEditMode && userInfo?.shirtSize;

  // Define shirt sizes for the dropdown
  const shirtSizes = [
    { value: "XS", label: "XS" },
    { value: "S", label: "S" },
    { value: "M", label: "M" },
    { value: "L", label: "L" },
    { value: "XL", label: "XL" },
    { value: "2XL", label: "2XL" },
    { value: "3XL", label: "3XL" },
  ];

  useEffect(() => {
    if (userInfo) {
      setPhone(userInfo.phone || "");
      setSex(userInfo.sex || "male");
      setShirtSize(userInfo.shirtSize || "M");
      setInterestedEvents(userInfo.interestedEvents || "");
      setReferredBy(userInfo.referredBy || "");
    }
  }, [userInfo]);

  useEffect(() => {
    // Populate event types checkbox states
    const eventTypes = getEventTypes();
    const initialState = {};
    eventTypes.forEach((type) => {
      initialState[type] = false;
    });
    setSelectedEventTypes(initialState);
  }, []);

  const getEventTypes = () => {
    return [
      "volleyball",
      "pickleball",
      "basketball",
      "dance",
      "yoga",
      "hiking",
      "biking",
      "skating",
      "running",
      "kayaking",
      "frisbee",
      "learning new languages",
      "learning new skills",
      "game nights",
      "beach hangouts",
      "paddle boarding",
    ];
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      if (!phone) {
        setError("Phone number is required");
        setLoading(false);
        return;
      }

      // Convert selected event types to string
      const selectedEvents = Object.keys(selectedEventTypes)
        .filter((key) => selectedEventTypes[key])
        .join(", ");

      const finalInterestedEvents =
        selectedEvents + (interestedEvents ? `, ${interestedEvents}` : "");

      // Use the GraphQL client to update the user profile
      const updatedProfile = await updateUserProfile({
        phone: phone,
        sex: sex,
        shirtSize: shirtSize,
        interestedEvents: finalInterestedEvents,
        referredBy: referredBy,
      });

      // Provide user information to parent component
      onSubmit({
        phone: updatedProfile.phone,
        sex: updatedProfile.sex,
        shirtSize: updatedProfile.shirtSize,
        interestedEvents: updatedProfile.interestedEvents,
        referredBy: updatedProfile.referredBy,
      });
    } catch (err) {
      console.error("Error submitting info:", err);
      setError("Failed to save information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return phone && phone.length > 0;
      default:
        return true;
    }
  };

  const handleEventTypeChange = (eventType) => {
    setSelectedEventTypes((prev) => ({
      ...prev,
      [eventType]: !prev[eventType],
    }));
  };

  const formChange = (field, value) => {
    switch (field) {
      case "phone":
        setPhone(value);
        break;
      case "sex":
        setSex(value);
        break;
      case "shirtSize":
        setShirtSize(value);
        break;
      default:
        break;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-group">
            {isPhoneReadOnly ? (
              <div className="readonly-field">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  className="form-control readonly"
                  id="phone"
                  value={phone}
                  readOnly
                />
                <small>
                  Phone number cannot be changed after the initial setup.
                </small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  id="phone"
                  placeholder="Phone"
                  value={phone}
                  onChange={(e) => formChange("phone", e.target.value)}
                />
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <>
            <div className="form-group">
              {isSexReadOnly ? (
                <div className="readonly-field">
                  <label htmlFor="sex">Sex</label>
                  <input
                    type="text"
                    className="form-control readonly"
                    id="sex"
                    value={sex}
                    readOnly
                  />
                  <small>Sex cannot be changed after the initial setup.</small>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="sex">Sex</label>
                  <div>
                    <Radio.Group
                      id="sex"
                      name="sex"
                      value={sex}
                      className="d-flex justify-content-around w-100"
                      onChange={(e) => formChange("sex", e.target.value)}
                    >
                      <Radio value={"male"}>Male</Radio>
                      <Radio value={"female"}>Female</Radio>
                    </Radio.Group>
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <Form.Item
                label="Shirt Size"
                className={classnames({
                  "form-right-align": true,
                  "has-error":
                    userInfo?.shirtSize === "" &&
                    userInfo?.shirtSize !== undefined,
                  "readonly-field": shouldDisable,
                })}
              >
                {shouldDisable ? (
                  <div>
                    {userInfo.shirtSize}
                    <div className="field-message">
                      Shirt size cannot be changed after initial setup.
                    </div>
                  </div>
                ) : (
                  <Select
                    placeholder="Select Shirt Size"
                    style={{ width: "100%" }}
                    value={shirtSize}
                    onChange={(value) => {
                      formChange("shirtSize", value);
                    }}
                  >
                    {shirtSizes.map((size) => (
                      <Select.Option key={size.value} value={size.value}>
                        {size.label}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </div>
          </>
        );
      case 3:
        return (
          <div className="form-group">
            <label>What do you like to do?</label>
            <div className="event-types-grid">
              {getEventTypes().map((type, i) => (
                <button
                  key={i}
                  type="button"
                  className={`event-type-button ${
                    selectedEventTypes[type] ? "selected" : ""
                  }`}
                  onClick={() => handleEventTypeChange(type)}
                >
                  {type}
                </button>
              ))}
            </div>
            <div className="form-group">
              <label htmlFor="otherInterests">Other interests</label>
              <input
                type="text"
                className="form-control"
                id="otherInterests"
                placeholder="Enter other interests, separated by commas"
                value={interestedEvents}
                onChange={(e) => setInterestedEvents(e.target.value)}
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="form-group">
            <label htmlFor="referredBy">Referred By</label>
            <input
              type="text"
              className="form-control"
              id="referredBy"
              placeholder="Referred By"
              value={referredBy}
              onChange={(e) => setReferredBy(e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // NOTE: the Submit button goes through handleSubmit (above), which
  // persists via the updateUserProfile mutation BEFORE notifying the
  // parent. A previous version notified the parent only — answers
  // looked saved but never reached the server.

  // Function to handle form changes
  const handleMentionTagClick = (item) => {
    console.log("Mention tag clicked:", item);
  };

  return (
    <div className="user-info-modal-overlay">
      <div className="user-info-modal">
        <div className="modal-header">
          <h3>
            {featureType === "chats"
              ? "Access Group Chats"
              : featureType === "profile"
              ? "Edit Your Profile"
              : featureType === "subscribe"
              ? "Unlock Membership Options"
              : "See Who's Attending"}
          </h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="modal-description">
            {featureType === "chats"
              ? "To access our group chats, we need a little more information:"
              : featureType === "profile"
              ? "Edit your profile information:"
              : featureType === "subscribe"
              ? "Before we show membership options, we need a little more information — it determines what you get:"
              : "To see who's attending this event, we need a little more information:"}
          </p>

          <div className="step-indicator">
            Step {currentStep} of {totalSteps}
          </div>

          {renderStep()}

          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="modal-footer">
          {currentStep > 1 && (
            <button
              className="btn-secondary"
              onClick={prevStep}
              disabled={loading}
            >
              Back
            </button>
          )}

          {currentStep < totalSteps ? (
            <button
              className="btn-primary"
              onClick={nextStep}
              disabled={!validateStep() || loading}
            >
              Next
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfoModal;
